import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';

import { Subscription, tap } from 'rxjs';

import { PlanTier } from '@shared/price-plan/enums';
import { PricePlan } from '@shared/price-plan/models';
import { SubscriptionState } from '@shared/recurly/enums';
import { AccountRecurly } from '@shared/account/models';
import { LocationRecurly } from '@shared/restaurant/models';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { AppTheme, ThemeSwitchService } from '@tablesready/frontend-theme-switch';
import { PlanLimit } from '../../models';
import { PLAN_TIERS, PLANS_BENEFITS } from '../../constants';

@Component({
  selector: 'payment-plan',
  templateUrl: './payment-plan.component.html',
  styleUrls: [
    './payment-plan.component.scss',
    '../../scss/plan-cards.scss',
  ],
})
export class PaymentPlanComponent implements OnInit, OnDestroy {
  @Input() currentPricePlan: PricePlan;
  @Input() selectedPricePlan: PricePlan;
  @Input() limits: PlanLimit[] = [];
  @Input() subscriptionState: SubscriptionState;
  @Input() accountRecurly: AccountRecurly;
  @Input() locationRecurly: LocationRecurly;
  @Input() blockActions = false;
  @Input() isNotUsaOrCanadaRestaurant: boolean;
  @Input() canceledSubscription = false;
  @Input() calcSavePercentageForAnnual: (tier: PlanTier, limit: number) => number;

  @Output() switchPlans = new EventEmitter<string>();
  @Output() limitSelected = new EventEmitter<number>();
  @Output() activate = new EventEmitter<string>();
  @Output() pause = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() reactivate = new EventEmitter<void>();

  form: FormGroup;
  planTiers = PlanTier;
  planTiersMap = PLAN_TIERS;
  benefits = PLANS_BENEFITS;
  theme: AppTheme;
  icon = AppIcon;
  iconColor = AppIconColor;
  iconSize = AppIconSize;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly themeService: ThemeSwitchService,
  ) { }

  ngOnInit(): void {
    this.theme = this.themeService.getSavedTheme();
    this.form = this.formBuilder.group({ selectedLimit: [this.selectedPricePlan.limit] });

    const limitChangedSub = this.selectedLimitControl.valueChanges
      .pipe(
        tap((value: number) => this.limitSelected.emit(value)),
      ).subscribe();
    this.subscriptions.add(limitChangedSub);
  }

  get showReactivateBtn(): boolean {
    return this.subscriptionState === SubscriptionState.canceled ||
      this.canceledSubscription ||
      this.paused;
  }

  get periodChanged(): boolean {
    return this.currentPricePlan.plan_interval_length !== this.selectedPricePlan.plan_interval_length;
  }

  get showPauseAndCancelButtons(): boolean {
    return !this.locationIsOnTrial &&
      this.currentPricePlan.tier !== PlanTier.free &&
      this.currentPricePlan.plan_code === this.selectedPricePlan.plan_code;
  }

  get locationIsOnTrial(): boolean {
    return this.locationRecurly.trial === true;
  }

  get showSubscribeNowBtn(): boolean {
    return this.currentPricePlan.tier !== PlanTier.free && this.locationIsOnTrial;
  }

  get tierChanged(): boolean {
    return this.currentPricePlan.tier !== this.selectedPricePlan.tier
  }

  get showUpgradeBtn(): boolean {
    return !this.tierChanged
      && !this.periodChanged
      && !this.showSubscribeNowBtn
      && this.selectedPricePlan.limit > this.currentPricePlan.limit;
  }

  get showDowngradeBtn(): boolean {
    return !this.tierChanged
      && !this.periodChanged
      && !this.showSubscribeNowBtn
      && this.currentPricePlan.limit > this.selectedPricePlan.limit;
  }

  get planPeriod(): string {
    return this.selectedPricePlan.plan_interval_length === 12 ? 'annual' : 'monthly';
  }

  get showSwitchPlansBtn(): boolean {
    return !this.showSubscribeNowBtn &&
      !this.periodChanged &&
      !this.showUpgradeBtn &&
      !this.showDowngradeBtn &&
      !this.showPauseAndCancelButtons;
  }

  get selectedLimitControl(): AbstractControl {
    return this.form.get('selectedLimit');
  }

  get everythingInText(): string | null {
    if (this.selectedPricePlan.tier !== PlanTier.starter && this.selectedPricePlan.tier !== PlanTier.business) {
      return null;
    }

    return this.selectedPricePlan.tier === PlanTier.starter
      ? 'Everything in Free, plus:'
      : 'Everything in Starter, plus:';
  }

  get canceled(): boolean {
    return this.subscriptionState === SubscriptionState.canceled;
  }

  get paused(): boolean {
    return this.subscriptionState === SubscriptionState.paused;
  }

  getPrice(pricePlan: PricePlan): number {
    if (!pricePlan) {
      return 0;
    }

    if (pricePlan?.plan_interval_length === 12) {
      return Math.ceil((pricePlan.price / 100) / 12);
    }

    return pricePlan.price / 100;
  }

  ngOnDestroy(): void {
    this.switchPlans.complete();
    this.limitSelected.complete();
    this.activate.complete();
    this.pause.complete();
    this.cancel.complete();
    this.subscriptions.unsubscribe();
  }
}
